const ExportService = {
  removeTranslationMeta(data: any): any {
    if (Array.isArray(data)) {
      return data.map((item) => this.removeTranslationMeta(item));
    }
    if (data !== null && typeof data === "object") {
      const { translation_meta, ...rest } = data;
      return Object.fromEntries(
        Object.entries(rest).map(([k, v]) => [
          k,
          this.removeTranslationMeta(v),
        ]),
      );
    }
    return data;
  },

  filterLocaleColumns(
    flattened: Record<string, any>[],
    locale: string,
  ): Record<string, any>[] {
    const localePattern = /^(.+)\.([a-z]{2})$/;
    const allowedSuffixes = new Set(["en"]);
    if (locale !== "en") allowedSuffixes.add(locale);
    const keepKey = (key: string): boolean => {
      const match = key.match(localePattern);
      if (!match) return true;
      return allowedSuffixes.has(match[2]);
    };
    return flattened.map((row) =>
      Object.fromEntries(Object.entries(row).filter(([k]) => keepKey(k))),
    );
  },

  async fetchResults(
    baseUrl: string,
    params: Record<string, any> = {},
  ): Promise<any> {
    const url = new URL(baseUrl);
    const { filter, ...restParams } = params;
    url.searchParams.set("q", "");

    if (filter) {
      if (typeof filter === "object" && !Array.isArray(filter)) {
        Object.entries(filter).forEach(([k, v]) =>
          url.searchParams.set(k, String(v)),
        );
      } else {
        const value = Array.isArray(filter) ? filter.join(",") : String(filter);
        const paramName = value.includes(",") ? "filters" : "filter";
        url.searchParams.set(paramName, value);
      }
    }

    Object.entries(restParams).forEach(([k, v]) => {
      if (v !== undefined && v !== null) {
        url.searchParams.set(k, String(v));
      }
    });

    const response = await fetch(url.toString());
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    return this.removeTranslationMeta(data);
  },
  extractDistributionIds(distributions: any[]): string {
    return distributions
      .map((v: any) => {
        if (typeof v === "object" && v !== null) return v.id ?? "";
        try {
          const parsed = JSON.parse(v);
          return parsed?.id ?? "";
        } catch {
          return "";
        }
      })
      .filter(Boolean)
      .join("; ");
  },

  flattenObject(
    obj: Record<string, any>,
    prefix = "",
    result: Record<string, any> = {},
  ): Record<string, any> {
    for (const [key, value] of Object.entries(obj)) {
      const newKey = prefix ? `${prefix}.${key}` : key;
      if (value === null || value === undefined) {
        result[newKey] = "";
      } else if (Array.isArray(value)) {
        if (key === "distributions") {
          result[newKey] = this.extractDistributionIds(value);
        } else {
          result[newKey] = value
            .map((v: any) =>
              typeof v === "object"
                ? JSON.stringify(v).replace(/\r?\n|\r/g, " ")
                : String(v).replace(/\r?\n|\r/g, " "),
            )
            .join("; ");
        }
      } else if (typeof value === "object") {
        this.flattenObject(value, newKey, result);
      } else {
        result[newKey] =
          typeof value === "string" ? value.replace(/\r?\n|\r/g, " ") : value;
      }
    }
    return result;
  },

  toDelimited(data: any, delimiter: string, locale = "en"): string {
    const results: any[] = data?.result?.results || data;
    if (!Array.isArray(results) || results.length === 0) return "";
    const flattened = results.map((item) => this.flattenObject(item));
    const localeFiltered = this.filterLocaleColumns(flattened, locale);
    const headers = [...new Set(localeFiltered.flatMap(Object.keys))];
    const escape = (val: any): string => {
      const str = String(val ?? "").replace(/\r?\n|\r/g, " ");
      return str.includes(delimiter) || str.includes('"')
        ? `"${str.replace(/"/g, '""')}"`
        : str;
    };
    return [
      headers.join(delimiter),
      ...localeFiltered.map((row) =>
        headers.map((h) => escape(row[h] ?? "")).join(delimiter),
      ),
    ].join("\n");
  },

  toCSV(data: any, locale = "en"): string {
    return this.toDelimited(data, ",", locale);
  },
  toTSV(data: any, locale = "en"): string {
    return this.toDelimited(data, "\t", locale);
  },

  toJSON(data: any): string {
    const results = data?.result?.results || data;
    return JSON.stringify(results, null, 2);
  },

  download(content: string, filename: string, mimeType: string): void {
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement("a"), {
      href: url,
      download: filename,
    });
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  async exportFromApi(
    baseUrl: string,
    params: Record<string, string | number> = {},
    format = "json",
    locale = "en",
  ): Promise<void> {
    const data = await this.fetchResults(baseUrl, params); // ← passes baseUrl through
    const filename = `export_${Date.now()}`;
    const formatMap: Record<string, () => void> = {
      csv: () =>
        this.download(this.toCSV(data, locale), `${filename}.csv`, "text/csv"),
      tsv: () =>
        this.download(
          this.toTSV(data, locale),
          `${filename}.tsv`,
          "text/tab-separated-values",
        ),
      json: () =>
        this.download(
          this.toJSON(data),
          `${filename}.json`,
          "application/json",
        ),
    };
    const handler = formatMap[format];
    if (!handler) throw new Error(`Unsupported format: ${format}`);
    handler();
  },
};

export default ExportService;
